// ============================================
// 1. OpenTrades.js - Complete Open Positions
// ============================================

import React, { useState, useEffect } from 'react';

const OpenTrades = () => {
  const [openPositions, setOpenPositions] = useState([
    { 
      id: 1,
      ticker: 'TSLA', 
      shares: 50, 
      entry: 237.60, 
      current: 235.85, 
      pnl: -87.50, 
      pnlPercent: -0.74,
      aiScore: 7.2,
      strategy: 'Options Core',
      stopLoss: 225.50,
      target: 255.00,
      daysHeld: 2,
      entryCatalyst: 'Q3 Earnings Beat + FSD Progress Announcement',
      exitPrice: null,
      exitDate: null,
      expirationDate: '2025-09-15',
      optionType: 'Call $240',
      entryDate: '2025-08-01',
      volume: 15420000,
      ivRank: 68,
      delta: 0.45,
      riskAmount: 600,
      confidenceLevel: 'Medium'
    },
    { 
      id: 2,
      ticker: 'LMT', 
      shares: 75, 
      entry: 410.25, 
      current: 422.10, 
      pnl: 888.75, 
      pnlPercent: 2.89,
      aiScore: 9.1,
      strategy: 'Government Intel',
      stopLoss: 395.00,
      target: 450.00,
      daysHeld: 1,
      entryCatalyst: 'Congressional Defense Spending Bill Passed',
      exitPrice: null,
      exitDate: null,
      expirationDate: null,
      optionType: null,
      entryDate: '2025-08-02',
      volume: 2150000,
      ivRank: null,
      delta: null,
      riskAmount: 1140,
      confidenceLevel: 'High'
    },
    { 
      id: 3,
      ticker: 'NVDA', 
      shares: 25, 
      entry: 445.80, 
      current: 448.25, 
      pnl: 61.25, 
      pnlPercent: 0.55,
      aiScore: 8.3,
      strategy: 'Phoenix Scalp',
      stopLoss: 432.50,
      target: 465.00,
      daysHeld: 0,
      entryCatalyst: 'Gap Up + Volume Surge at VWAP Reclaim',
      exitPrice: null,
      exitDate: null,
      expirationDate: null,
      optionType: null,
      entryDate: '2025-08-03',
      volume: 45230000,
      ivRank: null,
      delta: null,
      riskAmount: 333,
      confidenceLevel: 'High'
    }
  ]);

  // Helper Functions
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);

  const getAIScoreColor = (score) => {
    if (score >= 8.5) return '#00FF5F';
    if (score >= 7.0) return '#FFB800';
    return '#FF0000';
  };

  const getStrategyColor = (strategy) => {
    switch(strategy) {
      case 'Options Core': return '#D4AF37';
      case 'Government Intel': return '#0069B1';
      case 'Phoenix Scalp': return '#5BC0DE';
      default: return '#a0a9c1';
    }
  };

  const totalPnL = openPositions.reduce((sum, pos) => sum + pos.pnl, 0);
  const totalValue = openPositions.reduce((sum, pos) => sum + (pos.current * pos.shares), 0);

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif',
      padding: '24px'
    }}>
      {/* WATERMARK */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.03',
        pointerEvents: 'none',
        zIndex: '1',
        width: '800px',
        height: '800px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Financial Trading Watermark"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>

      <div style={{ position: 'relative', zIndex: '2', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0',
              fontFamily: 'Georgia, serif'
            }}>📈 Open Trades</h1>
            <p style={{
              color: '#a0a9c1',
              fontSize: '16px',
              margin: '8px 0 0 0',
              fontFamily: 'Georgia, serif'
            }}>Active positions and live performance tracking</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', color: '#a0a9c1', fontFamily: 'Georgia, serif' }}>Last Update</div>
            <div style={{ fontSize: '18px', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
              {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>

        {/* Portfolio Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Total Positions
            </div>
            <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
              {openPositions.length}
            </div>
          </div>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Total Value
            </div>
            <div style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
              {formatCurrency(totalValue)}
            </div>
          </div>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Unrealized P&L
            </div>
            <div style={{
              color: totalPnL > 0 ? '#00FF5F' : '#FF0000',
              fontSize: '24px',
              fontWeight: 'bold',
              fontFamily: 'Georgia, serif'
            }}>
              {formatCurrency(totalPnL)}
            </div>
          </div>
          <div style={{
            backgroundColor: '#202A44',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #3a4a6b',
            textAlign: 'center'
          }}>
            <div style={{ color: '#a0a9c1', fontSize: '14px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Options Positions
            </div>
            <div style={{ color: '#D4AF37', fontSize: '24px', fontWeight: 'bold', fontFamily: 'Georgia, serif' }}>
              {openPositions.filter(pos => pos.optionType).length}
            </div>
          </div>
        </div>

        {/* Position Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {openPositions.map((position, index) => (
            <div key={index} style={{
              backgroundColor: '#202A44',
              padding: '28px',
              borderRadius: '12px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #3a4a6b'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '24px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {position.ticker}
                  </div>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: getAIScoreColor(position.aiScore)
                  }}></div>
                  <div style={{ color: '#a0a9c1', fontSize: '16px', fontFamily: 'Georgia, serif' }}>
                    AI Score: {position.aiScore}/10
                  </div>
                  {position.optionType && (
                    <div style={{
                      backgroundColor: '#D4AF37',
                      color: '#202A44',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {position.optionType}
                    </div>
                  )}
                  <div style={{
                    backgroundColor: getStrategyColor(position.strategy),
                    color: '#ffffff',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {position.strategy}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#a0a9c1', fontSize: '14px', fontFamily: 'Georgia, serif' }}>
                      Unrealized P&L
                    </div>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '20px',
                      color: position.pnl > 0 ? '#00FF5F' : '#FF0000',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {formatCurrency(position.pnl)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: position.pnlPercent > 0 ? '#00FF5F' : '#FF0000',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {formatPercent(position.pnlPercent)}
                  </div>
                </div>
              </div>

              {/* Entry Catalyst */}
              <div style={{
                backgroundColor: '#1a2332',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #3a4a6b'
              }}>
                <div style={{
                  color: '#D4AF37',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontFamily: 'Georgia, serif'
                }}>
                  📈 Entry Catalyst
                </div>
                <div style={{
                  color: '#ffffff',
                  fontSize: '16px',
                  fontFamily: 'Georgia, serif',
                  lineHeight: '1.4'
                }}>
                  {position.entryCatalyst}
                </div>
              </div>

              {/* Price Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  backgroundColor: '#2B3554',
                  padding: '12px',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>
                    Entry Price
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(position.entry)}
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#2B3554',
                  padding: '12px',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>
                    Current Price
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(position.current)}
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#2B3554',
                  padding: '12px',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>
                    Target Price
                  </div>
                  <div style={{ color: '#00FF5F', fontSize: '16px', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(position.target)}
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#2B3554',
                  padding: '12px',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>
                    Stop Loss
                  </div>
                  <div style={{ color: '#FF0000', fontSize: '16px', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(position.stopLoss)}
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#2B3554',
                  padding: '12px',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>
                    Position Size
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                    {position.shares} shares
                  </div>
                </div>
              </div>

              {/* Details Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '12px',
                fontSize: '14px'
              }}>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Entry Date</div>
                  <div style={{ color: '#ffffff', fontFamily: 'Georgia, serif' }}>
                    {new Date(position.entryDate).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Days Held</div>
                  <div style={{ color: '#FFB800', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                    {position.daysHeld} days
                  </div>
                </div>
                {position.expirationDate && (
                  <div>
                    <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Expiration</div>
                    <div style={{ color: '#FFB800', fontWeight: '600', fontFamily: 'Georgia, serif' }}>
                      {new Date(position.expirationDate).toLocaleDateString()}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Risk Amount</div>
                  <div style={{ color: '#FF0000', fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(position.riskAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#a0a9c1', fontSize: '12px', fontFamily: 'Georgia, serif' }}>Confidence</div>
                  <div style={{
                    color: position.confidenceLevel === 'High' ? '#00FF5F' : 
                          position.confidenceLevel === 'Medium' ? '#FFB800' : '#FF0000',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {position.confidenceLevel}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OpenTrades;